// Downe Score — neonatal respiratory distress screening tool.
// Single-page form (5 criteria, each 0–2). Optimised for repeated bedside
// use during serial monitoring.
//
// Reference: Downes JJ, Vidyasagar D, Boggs TR Jr, Morrow GM.
// Respiratory distress syndrome of newborn infants. Clin Pediatr. 1970;9:325–331.
// Interpretation thresholds per PONED (Pelayanan Obstetri Neonatal Emergensi Dasar) standard.

import React, { useState, useMemo } from "react";
import { Wind, Copy, CheckCircle2, RotateCcw, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

const CRITERIA = [
  {
    key: "rr",
    en: "Respiratory rate",
    id: "Frekuensi napas",
    options: [
      { score: 0, en: "< 60 / min", id: "< 60 / menit" },
      { score: 1, en: "60–80 / min", id: "60–80 / menit" },
      { score: 2, en: "> 80 / min", id: "> 80 / menit" },
    ],
  },
  {
    key: "retract",
    en: "Retractions",
    id: "Retraksi",
    options: [
      { score: 0, en: "None", id: "Tidak ada" },
      { score: 1, en: "Mild", id: "Ringan" },
      { score: 2, en: "Severe", id: "Berat" },
    ],
  },
  {
    key: "cyanosis",
    en: "Cyanosis",
    id: "Sianosis",
    options: [
      { score: 0, en: "None", id: "Tidak ada" },
      { score: 1, en: "Resolves with O₂", id: "Hilang dengan O₂" },
      { score: 2, en: "Persists despite O₂", id: "Menetap meski dengan O₂" },
    ],
  },
  {
    key: "air",
    en: "Air entry",
    id: "Masuknya udara",
    options: [
      { score: 0, en: "Good", id: "Baik" },
      { score: 1, en: "Decreased", id: "Menurun" },
      { score: 2, en: "Barely audible / absent", id: "Nyaris tidak terdengar / tidak ada" },
    ],
  },
  {
    key: "grunt",
    en: "Grunting",
    id: "Merintih (grunting)",
    options: [
      { score: 0, en: "None", id: "Tidak ada" },
      { score: 1, en: "Audible with stethoscope", id: "Terdengar dengan stetoskop" },
      { score: 2, en: "Audible without stethoscope", id: "Terdengar tanpa stetoskop" },
    ],
  },
];

const SCORE_STYLE = {
  0: {
    idle: "border-teal-200 bg-teal-50/50 text-teal-800 hover:bg-teal-100",
    active: "border-teal-600 bg-teal-100 text-teal-900 ring-2 ring-teal-600/30",
    chip: "bg-teal-600 border-teal-700 text-white",
  },
  1: {
    idle: "border-amber-200 bg-amber-50/60 text-amber-800 hover:bg-amber-100",
    active: "border-amber-500 bg-amber-100 text-amber-900 ring-2 ring-amber-500/40",
    chip: "bg-amber-500 border-amber-600 text-white",
  },
  2: {
    idle: "border-red-200 bg-red-50/60 text-red-700 hover:bg-red-100",
    active: "border-red-600 bg-red-100 text-red-900 ring-2 ring-red-600/40",
    chip: "bg-red-600 border-red-700 text-white",
  },
};

function interpret(total, isID) {
  if (total <= 3) {
    return {
      band: "mild",
      color: "teal",
      title: isID ? "Distres napas ringan" : "Mild respiratory distress",
      note: isID
        ? "Pantau ketat. Berikan oksigen suplementasi bila SpO₂ < 90%."
        : "Close monitoring. Supplemental O₂ if SpO₂ < 90%.",
    };
  }
  if (total <= 5) {
    return {
      band: "moderate",
      color: "amber",
      title: isID ? "Distres napas sedang" : "Moderate respiratory distress",
      note: isID
        ? "Berikan oksigen. Pertimbangkan CPAP dan konsultasi neonatologi."
        : "Give O₂. Consider CPAP and neonatology consult.",
    };
  }
  return {
    band: "severe",
    color: "red",
    title: isID ? "Distres napas berat" : "Severe respiratory distress",
    note: isID
      ? "Pertimbangkan intubasi. Aktifkan tim resusitasi neonatal segera."
      : "Consider intubation. Activate neonatal resuscitation team immediately.",
  };
}

const RESULT_COLOR = {
  teal: "border-teal-600 bg-teal-50 text-teal-900",
  amber: "border-amber-500 bg-amber-50 text-amber-900",
  red: "border-red-600 bg-red-50 text-red-900",
};

const CHIP_COLOR = {
  teal: "bg-teal-600 text-white",
  amber: "bg-amber-500 text-white",
  red: "bg-red-600 text-white",
};

export default function Downe() {
  const { lang } = useLanguage();
  const isID = lang === "id";
  const [scores, setScores] = useState({});
  const [copied, setCopied] = useState(false);

  const setScore = (key, value) => setScores((s) => ({ ...s, [key]: value }));

  const total = useMemo(
    () => CRITERIA.reduce((a, c) => a + (scores[c.key] ?? 0), 0),
    [scores]
  );
  const filledCount = Object.keys(scores).length;
  const allFilled = filledCount === CRITERIA.length;
  const result = allFilled ? interpret(total, isID) : null;

  const reset = () => {
    setScores({});
    setCopied(false);
  };

  const copyReport = () => {
    const lines = [
      `Downe Score: ${total}`,
      result ? result.title : "",
      "",
      ...CRITERIA.map((c) => {
        const s = scores[c.key];
        const opt = c.options.find((o) => o.score === s);
        return `  ${isID ? c.id : c.en}: ${s ?? "—"}${opt ? " — " + (isID ? opt.id : opt.en) : ""}`;
      }),
    ];
    navigator.clipboard?.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="downe-page">
      {/* Header */}
      <header className="border-b border-stone-200 pb-5 mb-6">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-teal-700 mb-2 flex items-center gap-2">
          <Wind size={14} strokeWidth={1.5} />
          {isID ? "Neonatologi · Skrining Distres Napas" : "Neonatology · Respiratory Distress Screening"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {isID ? "Skor Downe" : "Downe Score"}
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {isID
            ? "Penilaian cepat distres napas neonatal — 5 kriteria, masing-masing 0–2. Dipakai berulang untuk monitor serial."
            : "Rapid neonatal respiratory distress screening — 5 criteria, each 0–2. Repeated at bedside for serial monitoring."}
        </p>
      </header>

      {/* Criteria form */}
      <div className="space-y-4" data-testid="downe-form">
        {CRITERIA.map((c) => {
          const label = isID ? c.id : c.en;
          const selected = scores[c.key];
          return (
            <div key={c.key} className="border border-stone-200 bg-white rounded-md p-4 md:p-5" data-testid={`downe-criterion-${c.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-base md:text-lg font-medium text-stone-900">{label}</div>
                <div className="text-[10px] tracking-[0.16em] uppercase font-mono text-stone-400">
                  {selected === undefined ? "—" : selected} / 2
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {c.options.map((opt) => {
                  const active = selected === opt.score;
                  const style = SCORE_STYLE[opt.score];
                  return (
                    <button
                      key={opt.score}
                      type="button"
                      onClick={() => setScore(c.key, opt.score)}
                      aria-pressed={active}
                      data-testid={`downe-${c.key}-${opt.score}`}
                      className={cn(
                        "border-2 rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors flex items-center gap-3 min-h-[52px]",
                        active ? style.active : style.idle
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-mono font-bold border shrink-0",
                          style.chip
                        )}
                      >
                        {opt.score}
                      </span>
                      <span className="leading-tight">{isID ? opt.id : opt.en}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Result */}
      <div className="mt-8" data-testid="downe-result-wrap">
        {result ? (
          <div className={cn("border-2 rounded-md p-5 md:p-6", RESULT_COLOR[result.color])} data-testid="downe-result">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "h-14 w-14 md:h-16 md:w-16 rounded-full flex items-center justify-center font-display text-2xl md:text-3xl font-semibold",
                    CHIP_COLOR[result.color]
                  )}
                  data-testid="downe-total"
                >
                  {total}
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.16em] uppercase font-mono opacity-70">
                    {isID ? "Total / 10" : "Total / 10"}
                  </div>
                  <div className="font-display text-lg md:text-xl font-medium mt-0.5">
                    {result.title}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={copyReport}
                data-testid="downe-copy"
                className={cn(
                  "inline-flex items-center gap-2 border-2 rounded-md px-4 py-2.5 min-h-[44px] text-sm font-medium transition-colors bg-white",
                  copied
                    ? "border-teal-700 bg-teal-700 text-white"
                    : "border-current hover:bg-current/10"
                )}
              >
                {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                {copied ? (isID ? "Tersalin!" : "Copied!") : isID ? "Salin ke rekam medis" : "Copy to record"}
              </button>
            </div>
            <div className="mt-4 text-sm leading-relaxed border-t border-current/20 pt-4">
              {result.note}
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-stone-300 bg-white rounded-md p-6 text-center text-sm text-stone-500" data-testid="downe-empty">
            {isID
              ? `Isi semua 5 kriteria untuk melihat hasil (${filledCount}/5 terisi).`
              : `Fill all 5 criteria to see the result (${filledCount}/5 filled).`}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={reset}
          data-testid="downe-reset"
          className="inline-flex items-center gap-2 border-2 border-stone-200 text-stone-500 hover:text-stone-800 hover:border-stone-400 rounded-md px-3 py-2 text-xs font-medium transition-colors"
        >
          <RotateCcw size={14} />
          {isID ? "Reset" : "Reset"}
        </button>
      </div>

      {/* Interpretation legend */}
      <div className="mt-8 border border-stone-200 bg-stone-50 rounded-md p-4" data-testid="downe-legend">
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-stone-500 mb-2 flex items-center gap-2">
          <AlertTriangle size={12} />
          {isID ? "Interpretasi (Standar PONED)" : "Interpretation (PONED standard)"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-6 w-10 rounded-sm bg-teal-600 text-white font-mono text-[11px]">≤ 3</span>
            <span>{isID ? "Distres ringan" : "Mild distress"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-6 w-10 rounded-sm bg-amber-500 text-white font-mono text-[11px]">4–5</span>
            <span>{isID ? "Distres sedang" : "Moderate distress"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-6 w-10 rounded-sm bg-red-600 text-white font-mono text-[11px]">≥ 6</span>
            <span>{isID ? "Distres berat — pertimbangkan intubasi" : "Severe distress — consider intubation"}</span>
          </div>
        </div>
      </div>

      {/* Disclaimer + reference */}
      <div className="mt-6 max-w-2xl space-y-3">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">
            {isID ? "Sebagai rujukan saja." : "Reference only."}
          </span>{" "}
          {isID
            ? "Ambang interpretasi mengikuti standar PONED. Skor ini adalah alat bantu skrining — bukan pengganti penilaian klinis langsung. Selalu ulangi penilaian secara serial."
            : "Interpretation thresholds follow the PONED standard. This score is a screening aid — not a substitute for direct clinical judgement. Always reassess serially."}
        </div>
        <div className="text-[11px] text-stone-500 leading-relaxed">
          {isID ? "Sumber: " : "Reference: "}
          Downes JJ, Vidyasagar D, Boggs TR Jr, Morrow GM. Respiratory distress syndrome of newborn infants. <em>Clin Pediatr</em>. 1970;9:325–331.
        </div>
      </div>
    </div>
  );
}
